import { useCallback, useState } from "react";
import { ChevronDown, Loader2, Plug, Plus } from "lucide-react";
import {
  createWorkspaceMCPToken,
  listWorkspaceMCPTokens,
  revokeWorkspaceMCPToken,
} from "../../lib/api";
import type { WorkspaceMCPToken } from "../../lib/api";
import { CopyButton } from "../CopyButton";

// Connect external MCP clients (Claude Code, Codex, Claude Desktop) to this
// workspace's integration tools: mint a workspace MCP token and point the
// client at the workspace-integrations MCP endpoint. Tokens are fetched
// lazily on first expand so the integrations page pays nothing by default.
export function ExternalAgentsCard({
  accountId,
  workspaceId,
  canManage,
}: {
  accountId: number;
  workspaceId?: string;
  canManage: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<WorkspaceMCPToken[]>([]);
  const [mcpUrl, setMcpUrl] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [freshToken, setFreshToken] = useState<{ secret: string; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listWorkspaceMCPTokens(accountId, workspaceId);
      setTokens(response.tokens);
      setMcpUrl(response.mcp_url);
      setLoaded(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load MCP tokens.");
    } finally {
      setLoading(false);
    }
  }, [accountId, workspaceId]);

  const handleToggle = useCallback(() => {
    setOpen((previous) => {
      const next = !previous;
      if (next && !loaded) void refresh();
      return next;
    });
  }, [loaded, refresh]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await createWorkspaceMCPToken(accountId, name.trim() || "external-agent", workspaceId);
      setFreshToken({ secret: response.token, url: response.mcp_url });
      setName("");
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create MCP token.");
    } finally {
      setCreating(false);
    }
  }, [accountId, name, refresh, workspaceId]);

  const handleRevoke = useCallback(async (tokenId: string) => {
    setRevokingId(tokenId);
    setError(null);
    try {
      await revokeWorkspaceMCPToken(accountId, tokenId, workspaceId);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to revoke MCP token.");
    } finally {
      setRevokingId(null);
    }
  }, [accountId, refresh, workspaceId]);

  const claudeCommand = freshToken
    ? `claude mcp add --transport http openclaw ${freshToken.url} --header "Authorization: Bearer ${freshToken.secret}"`
    : "";
  const codexConfig = freshToken
    ? `# ~/.codex/config.toml  (export OCM_MCP_TOKEN=${freshToken.secret})\n[mcp_servers.openclaw]\nurl = "${freshToken.url}"\nbearer_token_env_var = "OCM_MCP_TOKEN"`
    : "";

  return (
    <section className="rounded-[var(--radius-sm)] border border-border bg-card shadow-card">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <span className="flex items-center gap-2">
          <Plug className="h-4 w-4 text-text-tertiary" />
          <span className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">External agents (MCP)</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border p-4">
          <p className="text-sm text-text-secondary">
            Point Claude Code, Codex, or any MCP client at this workspace: it can discover tools with{" "}
            <code className="text-xs">ocm.search_tools</code>, inspect them with{" "}
            <code className="text-xs">ocm.describe_tool</code>, and run them with{" "}
            <code className="text-xs">ocm.call_tool</code> — under the same policies and approvals as your machines.
          </p>

          {error && (
            <div className="mt-3 rounded-[var(--radius-sm)] border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
              {error}
            </div>
          )}

          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading tokens...
            </div>
          ) : (
            <>
              {mcpUrl && (
                <div className="mt-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-xs text-text-secondary">
                  <span className="font-medium text-text-primary">MCP endpoint</span>
                  <code className="min-w-0 flex-1 truncate">{mcpUrl}</code>
                  <CopyButton text={mcpUrl} />
                </div>
              )}

              {freshToken && (
                <div className="mt-4 rounded-[var(--radius-sm)] border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs">
                  <div className="font-medium text-emerald-600">
                    Token created — copy it now, it will not be shown again.
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="min-w-0 flex-1 break-all text-text-primary">{freshToken.secret}</code>
                    <CopyButton text={freshToken.secret} />
                  </div>
                  <div className="mt-3 space-y-2">
                    <div>
                      <div className="mb-1 flex items-center justify-between text-text-secondary">
                        <span>Claude Code</span>
                        <CopyButton text={claudeCommand} />
                      </div>
                      <pre className="overflow-x-auto rounded bg-card p-2 text-[11px] text-text-primary">{claudeCommand}</pre>
                    </div>
                    <div>
                      <div className="mb-1 flex items-center justify-between text-text-secondary">
                        <span>Codex</span>
                        <CopyButton text={codexConfig} />
                      </div>
                      <pre className="overflow-x-auto rounded bg-card p-2 text-[11px] text-text-primary">{codexConfig}</pre>
                    </div>
                  </div>
                </div>
              )}

              {tokens.length > 0 && (
                <div className="mt-4 space-y-2">
                  {tokens.map((token) => (
                    <div
                      key={token.id}
                      className="flex flex-col gap-2 rounded-[var(--radius-sm)] border border-border bg-[var(--color-surface)] px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-text-primary">{token.name}</span>
                        <span className="text-text-tertiary"> · {token.token_prefix}…</span>
                        <div className="mt-0.5 text-text-tertiary">
                          {token.last_used_at ? `Last used ${new Date(token.last_used_at).toLocaleString()}` : "Never used"}
                        </div>
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => void handleRevoke(token.id)}
                          disabled={revokingId === token.id}
                          className="inline-flex flex-shrink-0 items-center rounded-[var(--radius-sm)] border border-red-500/40 px-2.5 py-1 font-medium text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canManage && (
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Token name (e.g. claude-code)"
                    className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-input px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-500 sm:max-w-72"
                  />
                  <button
                    type="button"
                    onClick={() => void handleCreate()}
                    disabled={creating}
                    className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Generate token
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
